import React, { useContext } from 'react';
import SectionBlock from './SectionBlock';
import { IconClockL } from '../../../icons';
import { availabilityTerms, frequencyTerms, MatchingDialogContext } from '../configs';
import { showSection } from '../utils';
import {
    PART_TIME_OPTION_FULL,
    RIDINGS_PER_WEEK_FLEXIBLE,
} from '../../../marketplace-custom-config';
import config from '../../../config';

const { listingTypeHorse, listingTypePrivateHorse } = config;

const Frequency = ({
    frequencyMatches,
    currentPageListingFrequencyTerm,
    currentPageListingFrequency,
    currentListingAvatar,
    viewerListingAvatar,
    viewerListingFrequencyTerm,
    viewerListingFrequency,
}) => {
    const { riderProfileImage, horseImage } = useContext(MatchingDialogContext);

    const currentPageListingIsFlexible = currentPageListingFrequency === RIDINGS_PER_WEEK_FLEXIBLE;
    const viewerListingIsFlexible = viewerListingFrequency === RIDINGS_PER_WEEK_FLEXIBLE;

    if (currentPageListingIsFlexible) {
        return (
            <SectionBlock.ScoreLine
                matches
                joinedAvatars
                avatars={
                    <>
                        <SectionBlock.ScoreLineAvatar avatarUrl={riderProfileImage} />
                        <SectionBlock.ScoreLineAvatar avatarUrl={horseImage} />
                    </>
                }
                lineLabel={
                    viewerListingFrequencyTerm
                        ? viewerListingFrequencyTerm(viewerListingFrequency)
                        : 'Keine'
                }
            />
        );
    }

    if (viewerListingIsFlexible) {
        return (
            <SectionBlock.ScoreLine
                matches
                joinedAvatars
                lineLabel={
                    currentPageListingFrequencyTerm
                        ? currentPageListingFrequencyTerm(currentPageListingFrequency)
                        : 'Keine'
                }
                avatars={
                    <>
                        <SectionBlock.ScoreLineAvatar avatarUrl={riderProfileImage} />
                        <SectionBlock.ScoreLineAvatar avatarUrl={horseImage} />
                    </>
                }
            />
        );
    }

    return (
        <>
            <SectionBlock.ScoreLine
                matches={frequencyMatches}
                lineLabel={
                    currentPageListingFrequencyTerm
                        ? currentPageListingFrequencyTerm(currentPageListingFrequency)
                        : 'Keine'
                }
                avatars={currentListingAvatar}
            />
            <SectionBlock.Delimiter />
            <SectionBlock.ScoreLine
                matches={frequencyMatches}
                avatars={viewerListingAvatar}
                lineLabel={
                    viewerListingFrequencyTerm
                        ? viewerListingFrequencyTerm(viewerListingFrequency)
                        : 'Keine'
                }
            />
        </>
    );
};

const FrequencyAreEqual = ({
    currentPageListingIsFlexible,
    viewerListingIsFlexible,
    commonFrequencyTerm,
    currentPageListingFrequency,
}) => {
    const { riderProfileImage, horseImage } = useContext(MatchingDialogContext);
    return (
        <SectionBlock.ScoreLine
            matches
            joinedAvatars
            lineLabel={
                currentPageListingIsFlexible && viewerListingIsFlexible
                    ? 'Flexibel'
                    : commonFrequencyTerm
                    ? commonFrequencyTerm(currentPageListingFrequency)
                    : 'Keine'
            }
            avatars={
                <>
                    <SectionBlock.ScoreLineAvatar avatarUrl={riderProfileImage} />
                    <SectionBlock.ScoreLineAvatar avatarUrl={horseImage} />
                </>
            }
        />
    );
};

const TermsAreEqual = ({ commonAvailabilityTerm, horseImage, riderProfileImage }) => (
    <SectionBlock.ScoreLine
        matches
        joinedAvatars
        lineLabel={commonAvailabilityTerm ? commonAvailabilityTerm() : 'Keine'}
        avatars={
            <>
                <SectionBlock.ScoreLineAvatar avatarUrl={riderProfileImage} />
                <SectionBlock.ScoreLineAvatar avatarUrl={horseImage} />
            </>
        }
    />
);

const TermsMatches = ({
    currentPageListingAvailabilityTerm,
    currentListingAvatar,
    viewerListingAvailabilityTerm,
    viewerListingAvatar,
}) => {
    const { riderListing, riderProfileImage, horseListing, horseImage } = useContext(
        MatchingDialogContext
    );

    const riderListingIsFullyOpen =
        riderListing?.attributes?.publicData?.openForPartTimeProposals === PART_TIME_OPTION_FULL;
    const horseListingTerm = horseListing?.attributes?.publicData?.openForPartTimeProposals;

    if (riderListingIsFullyOpen) {
        return (
            <SectionBlock.ScoreLine
                matches
                joinedAvatars
                lineLabel={
                    availabilityTerms[horseListingTerm]
                        ? availabilityTerms[horseListingTerm]()
                        : 'Keine'
                }
                avatars={
                    <>
                        <SectionBlock.ScoreLineAvatar avatarUrl={riderProfileImage} />
                        <SectionBlock.ScoreLineAvatar avatarUrl={horseImage} />
                    </>
                }
            />
        );
    }

    return (
        <>
            <SectionBlock.ScoreLine
                matches={false}
                lineLabel={
                    currentPageListingAvailabilityTerm
                        ? currentPageListingAvailabilityTerm()
                        : 'Keine'
                }
                avatars={currentListingAvatar}
            />
            <SectionBlock.Delimiter />
            <SectionBlock.ScoreLine
                matches={false}
                lineLabel={
                    viewerListingAvailabilityTerm ? viewerListingAvailabilityTerm() : 'Keine'
                }
                avatars={viewerListingAvatar}
            />
        </>
    );
};

const SectionAvailability = () => {
    const {
        scoreData,
        categorySelected,
        currentPageListing,
        viewerListing,
        horseImage,
        riderProfileImage,
        horseListing,
    } = useContext(MatchingDialogContext);
    const scoreAvailabilityData = scoreData?.baseScoreBreakdown?.availabilityScore;

    if (!scoreAvailabilityData) return null;

    const { shouldBeShown } = showSection({
        categorySelected,
        ...scoreAvailabilityData,
    });

    if (!shouldBeShown) return null;

    const currentPageListingPublicData = currentPageListing?.attributes?.publicData || {};
    const viewerListingPublicData = viewerListing?.attributes?.publicData || {};

    const currentListingIsHorseListing =
        currentPageListingPublicData.type === listingTypeHorse ||
        currentPageListingPublicData.type === listingTypePrivateHorse;
    const viewerListingIsHorseListing =
        viewerListingPublicData.type === listingTypeHorse ||
        viewerListingPublicData.type === listingTypePrivateHorse;

    const currentPageListingTerm = currentPageListingPublicData.openForPartTimeProposals;
    const viewerListingTerm = viewerListingPublicData.openForPartTimeProposals;

    const termsAreEqual = currentPageListingTerm === viewerListingTerm;

    const currentPageListingFrequency = currentPageListingPublicData.availability;
    const viewerListingFrequency = viewerListingPublicData.availability;
    const commonFrequency = currentPageListingFrequency || viewerListingFrequency;

    const currentPageListingIsFlexible = currentPageListingFrequency === RIDINGS_PER_WEEK_FLEXIBLE;
    const viewerListingIsFlexible = viewerListingFrequency === RIDINGS_PER_WEEK_FLEXIBLE;

    const horseListingIsFlexible =
        horseListing?.attributes?.publicData?.availability === RIDINGS_PER_WEEK_FLEXIBLE;

    const frequencyAreEqual =
        horseListingIsFlexible || currentPageListingFrequency === viewerListingFrequency;
    const frequencyMatches =
        !frequencyAreEqual && (currentPageListingIsFlexible || viewerListingIsFlexible);

    const viewerListingAvatar = (
        <SectionBlock.ScoreLineAvatar
            avatarUrl={viewerListingIsHorseListing ? horseImage : riderProfileImage}
        />
    );
    const currentListingAvatar = (
        <SectionBlock.ScoreLineAvatar
            avatarUrl={currentListingIsHorseListing ? horseImage : riderProfileImage}
        />
    );

    const currentPageListingAvailabilityTerm = availabilityTerms[currentPageListingTerm];
    const viewerListingAvailabilityTerm = availabilityTerms[viewerListingTerm];

    const currentPageListingFrequencyTerm = frequencyTerms[currentPageListingFrequency];
    const viewerListingFrequencyTerm = frequencyTerms[viewerListingFrequency];

    const commonFrequencyTerm = frequencyTerms[commonFrequency];
    const commonAvailabilityTerm = availabilityTerms[viewerListingTerm || currentPageListingTerm];

    return (
        <SectionBlock scoreData={scoreData}>
            <SectionBlock.Header>
                <IconClockL />
                <span>Verfügbarkeit</span>
            </SectionBlock.Header>
            <SectionBlock.ScoreBadge sectionScoreData={scoreAvailabilityData} />
            <section>
                {/* Frequency */}
                {frequencyAreEqual && (
                    <FrequencyAreEqual
                        commonFrequencyTerm={commonFrequencyTerm}
                        currentPageListingFrequency={currentPageListingFrequency}
                        currentPageListingIsFlexible={currentPageListingIsFlexible}
                        viewerListingIsFlexible={viewerListingIsFlexible}
                    />
                )}
                {!frequencyAreEqual && (
                    <Frequency
                        currentListingAvatar={currentListingAvatar}
                        currentPageListingFrequencyTerm={currentPageListingFrequencyTerm}
                        currentPageListingFrequency={currentPageListingFrequency}
                        frequencyMatches={frequencyMatches}
                        viewerListingAvatar={viewerListingAvatar}
                        viewerListingFrequencyTerm={viewerListingFrequencyTerm}
                        viewerListingFrequency={viewerListingFrequency}
                    />
                )}
                {/* Term */}
                {termsAreEqual && (
                    <TermsAreEqual
                        riderProfileImage={riderProfileImage}
                        commonAvailabilityTerm={commonAvailabilityTerm}
                        horseImage={horseImage}
                    />
                )}
                {!termsAreEqual && (
                    <TermsMatches
                        currentListingAvatar={currentListingAvatar}
                        currentPageListingAvailabilityTerm={currentPageListingAvailabilityTerm}
                        viewerListingAvailabilityTerm={viewerListingAvailabilityTerm}
                        viewerListingAvatar={viewerListingAvatar}
                    />
                )}
            </section>
        </SectionBlock>
    );
};

export default SectionAvailability;
